export const SPARKLINE_WIDTH = 100;
export const SPARKLINE_HEIGHT = 28;

const PADDING = 3;

export interface SparklinePoint {
  x: number;
  y: number;
}

export interface Sparkline {
  /** The SVG `points` attribute, or an empty string with fewer than two values. */
  points: string;
  /** The same line closed down to the bottom edge, for the tinted area under it. */
  area: string;
  last: SparklinePoint | null;
}

const EMPTY_SPARKLINE: Sparkline = { points: "", area: "", last: null };

/**
 * Lays the months out evenly across the width, so a month with no value
 * leaves its gap rather than pulling the next point in.
 */
export const buildSparkline = (values: (number | null)[]): Sparkline => {
  const present = values.filter((value): value is number => value !== null);

  if (present.length === 0) {
    return EMPTY_SPARKLINE;
  }

  const min = Math.min(...present);
  const max = Math.max(...present);
  const step =
    values.length > 1
      ? (SPARKLINE_WIDTH - PADDING * 2) / (values.length - 1)
      : 0;

  const placed: SparklinePoint[] = [];

  values.forEach((value, index) => {
    if (value === null) {
      return;
    }

    placed.push({
      x: round(PADDING + index * step),
      y: round(scaleY(value, min, max)),
    });
  });

  const first = placed[0];
  const last = placed[placed.length - 1] ?? null;

  if (placed.length < 2) {
    return { points: "", area: "", last };
  }

  const points = placed.map((point) => `${point.x},${point.y}`).join(" ");

  return {
    points,
    area: `${points} ${last.x},${SPARKLINE_HEIGHT} ${first.x},${SPARKLINE_HEIGHT}`,
    last,
  };
};

/** A flat series sits on the middle line rather than the floor. */
const scaleY = (value: number, min: number, max: number): number => {
  if (max === min) {
    return SPARKLINE_HEIGHT / 2;
  }

  const usable = SPARKLINE_HEIGHT - PADDING * 2;

  return PADDING + usable - ((value - min) / (max - min)) * usable;
};

const round = (value: number): number => Math.round(value * 10) / 10;
